import { FiSearch } from "react-icons/fi";
import styled, { css, keyframes } from "styled-components";

// Animations from AdminProductsPage
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- Layout wrappers ---
export const Main = styled.main`
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  padding-top: 4rem;
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 2rem;
  position: relative;
  width: 100%;
  &&::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="%23ff6b35" opacity="0.1"/><circle cx="75" cy="75" r="1.5" fill="%23ff6b35" opacity="0.1"/><circle cx="50" cy="10" r="0.8" fill="%23ff6b35" opacity="0.1"/><circle cx="10" cy="60" r="1.2" fill="%23ff6b35" opacity="0.1"/><circle cx="90" cy="30" r="0.9" fill="%23ff6b35" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
    z-index: 1;
  }

  @media (max-width: 1400px) {
    grid-template-columns: 250px 1fr;
    gap: 1rem;
  }
  @media (max-width: 1330px) {
    grid-template-columns: 1fr;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`;

export const Sidebar = styled.aside`
  background: #1a1a1a;
  border-radius: 15px;
  padding: 2rem;
  height: fit-content;
  z-index: 2;
  position: sticky;
  top: 120px;

  @media (max-width: 1330px) {
    display: none;
  }
`;

export const Section = styled.section`
  background: #1a1a1a;
  border-radius: 15px;
  z-index: 2;
  padding: 20px;
  position: relative;
  width: 100%;
  max-width: 1004px;
  min-height: 665.5px;
`;

// --- Sidebar controls ---
export const FilterSection = styled.div`
  margin-bottom: 2rem;
`;

export const FilterTitle = styled.h3`
  color: #ff6b35;
  font-size: 1.2rem;
  font-family: "Poppins-Bold";
  margin-bottom: 1rem;
  border-bottom: 2px solid #ff6b35;
  padding-bottom: 0.5rem;
`;

export const GenreButton = styled.button`
  display: block;
  width: 100%;
  padding: 0.8rem;
  margin-bottom: 0.5rem;
  background: #2c2c2c;
  color: #ffffff;
  border: 1px solid #333;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  text-align: left;
  font-family: "Poppins-Medium";

  ${({ $active }) =>
    $active &&
    css`
      background: #ff6b35;
      color: #1a1a1a;
      border-color: #ff6b35;
    `}

  &:hover {
    background: #ff6b35;
    color: #1a1a1a;
    border-color: #ff6b35;
  }
`;

export const BandList = styled.div`
  max-height: 350px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #ff6b35 #1a1a1a;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #1a1a1a;
  }
  &::-webkit-scrollbar-thumb {
    background: #ff6b35;
    border-radius: 3px;
  }
`;

export const BandItem = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.8rem;
  margin-bottom: 0.5rem;
  background: #2c2c2c;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  color: #fff;
  text-align: left;

  &:hover {
    background: #333;
    border-color: #ff6b35;
    transform: translateX(5px);
  }

  ${({ $active }) =>
    $active &&
    css`
      background: #ff6b35;
      color: #1a1a1a;
      font-family: "Poppins-Bold";
    `}
`;

export const BandName = styled.span`
  font-size: 0.9rem;
`;

export const CountPill = styled.div`
  background: #ff6b35;
  color: #fff;
  border: 1px solid #fff;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Poppins-Bold";
`;

export const ClearFilters = styled.button`
  background: #333;
  color: #ffffff;
  border: none;
  padding: 0.8rem;
  border-radius: 8px;
  cursor: pointer;
  width: 100%;
  margin-top: 1rem;
  transition: background 0.3s ease;
  font-family: "Poppins-Medium";

  &:hover {
    background: #555;
  }
`;

// --- Products header ---
export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const SectionTitle = styled.h2`
  font-size: 2rem;
  color: #ff6b35;
  font-family: "Poppins-Bold";
`;

export const ViewOptions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

export const SortSelect = styled.select`
  background: #2c2c2c;
  color: #ffffff;
  border: 1px solid #ff6b35;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-family: "Poppins-Medium";

  @media (max-width: 768px) {
    max-width: 300px;
    width: 100%;
  }
`;

export const ViewToggle = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const ViewBtn = styled.button`
  background: ${({ $active }) => ($active ? "#ff6b35" : "#2c2c2c")};
  color: #ffffff;
  border: 1px solid ${({ $active }) => ($active ? "#ff6b35" : "#333")};
  padding: 0.5rem;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const SearchWrap = styled.div`
  position: relative;
  width: 300px;
  @media (max-width: 768px) {
    width: 100%;
    max-width: 300px;
  }
`;

export const SearchInput = styled.input`
  background: #2c2c2c;
  color: #fff;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 0.6rem 2.2rem 0.6rem 0.8rem;
  width: 100%;

  max-width: 300px;
  font-family: "Poppins-Regular";

  &::placeholder {
    color: #aaa;
  }
`;

export const SearchIcon = styled(FiSearch)`
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

export const ProductsGrid = styled.div`
  column-count: ${({ $list }) => ($list ? 1 : 3)};
  column-gap: ${({ $list }) => ($list ? "1rem" : "2rem")};
  margin-bottom: 2rem;
  @media (max-width: 1500px) {
    column-count: ${({ $list }) => ($list ? 1 : 3)};
  }
  @media (max-width: 900px) {
    column-count: ${({ $list }) => ($list ? 1 : 2)};
  }
  @media (max-width: 768px) {
    column-count: 1;
  }
`;

// New ProductCard design from AdminProductsPage
export const ProductCard = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid #444;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;
  break-inside: avoid;
  width: 100%;
  margin-bottom: 1.5rem;
  min-width: 19rem;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #ff6b35, #e55a2b);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(255, 107, 53, 0.3);
    border-color: #ff6b35;
  }
`;

export const ProductImageContainer = styled.div`
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
  background: #222;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

export const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

export const Badge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: ${({ $variant }) =>
    $variant === "discount"
      ? "#e74c3c"
      : $variant === "limited"
      ? "#9b59b6"
      : $variant === "new"
      ? "#27ae60"
      : "#ff6b35"};
  color: #ffffff;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: bold;
  z-index: 10;
`;

export const ProductHeader = styled.div`
  margin-bottom: 1rem;
`;

export const ProductTitle = styled.h4`
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: bold;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
`;

export const ProductBand = styled.span`
  color: #ff6b35;
  font-size: 0.9rem;
  font-weight: 600;
`;

export const ProductMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
`;

export const ProductMetaItem = styled.div`
  display: flex;
  align-items: center;
`;

export const ProductCategory = styled.span`
  background: rgba(255, 107, 53, 0.2);
  color: #ff6b35;
  padding: 0.3rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
`;

export const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const ProductPrice = styled.span`
  color: ${(props) => (props.$discounted ? "#888" : "#27ae60")};
  font-weight: bold;
  font-size: ${(props) => (props.$discounted ? "0.9rem" : "1.1rem")};
  text-decoration: ${(props) => (props.$discounted ? "line-through" : "none")};
`;

export const ProductDiscountPrice = styled.span`
  color: #27ae60;
  font-weight: bold;
  font-size: 1.1rem;
`;

export const StockInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

export const StockBadge = styled.span`
  background: ${(props) =>
    props.$inStock ? "rgba(39, 174, 96, 0.2)" : "rgba(231, 76, 60, 0.2)"};
  color: ${(props) => (props.$inStock ? "#27ae60" : "#e74c3c")};
  padding: 0.3rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
`;

export const ProductStock = styled.span`
  color: #cccccc;
  font-size: 0.9rem;
`;

export const ProductSizes = styled.div`
  color: #ff6b35;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  font-weight: bold;
`;

export const ProductShortDesc = styled.p`
  color: #cccccc;
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0 0 1.5rem 0;

  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ProductActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

// Button component from AdminProductsPage
export const Button = styled.button`
  padding: 0.8rem 1.2rem;
  min-height: 3rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${(props) => {
    switch (props.variant) {
      case "primary":
        return "#ff6b35";
      case "secondary":
        return "#666";
      case "danger":
        return "#e74c3c";
      case "success":
        return "#27ae60";
      default:
        return "#ff6b35";
    }
  }};
  color: #ffffff;

  &:hover {
    background: ${(props) => {
      switch (props.variant) {
        case "primary":
          return "#e55a2b";
        case "secondary":
          return "#555";
        case "danger":
          return "#c0392b";
        case "success":
          return "#229954";
        default:
          return "#e55a2b";
      }
    }};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// Size selector from AdminProductsPage
export const SizeSelector = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

export const SizeOption = styled.button`
  background: ${(props) => (props.$selected ? "#ff6b35" : "#444")};
  color: ${(props) => (props.$selected ? "#ffffff" : "#cccccc")};
  border: 1px solid ${(props) => (props.$selected ? "#ff6b35" : "#666")};
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.65rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: ${(props) => (props.$selected ? "bold" : "normal")};

  &:hover {
    background: ${(props) => (props.$selected ? "#e55a2b" : "#555")};
    border-color: #ff6b35;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #333;
    color: #666;
    border-color: #333;
  }
`;

// Quantity controls
export const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  background: #2c2c2c;
  border-radius: 10px;
  border: 2px solid #444;
  overflow: hidden;
  max-height: 2rem;
  width: fit-content;
`;

export const QuantityBtn = styled.button`
  background: transparent;
  color: #ffffff;
  border: none;
  padding: 1rem;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.3s ease;
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;

  &:hover {
    background: #ff6b35;
  }

  &:disabled {
    color: #666;
    cursor: not-allowed;
    background: transparent;
  }
`;

export const QuantityInput = styled.input`
  background: transparent;
  color: #ffffff;
  border: none;
  padding: 1rem;
  width: 40px;
  text-align: center;
  font-weight: bold;
  font-size: 0.8rem;

  &:focus {
    outline: none;
  }

  &:disabled {
    color: #666;
  }
`;

// Legacy components from original ShopPage (keeping for compatibility)
export const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const ProductInfo = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: ${({ $list }) => ($list ? "space-between" : "initial")};
  flex: 1;
`;

export const BandTag = styled.span`
  background: #ff6b35;
  color: #ffffff;
  padding: 0.2rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-family: "Poppins-Bold";
  display: inline-block;
  margin-bottom: 0.5rem;
`;

export const ProductDesc = styled.p`
  color: #cccccc;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.4;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

export const PriceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const CurrentPrice = styled.span`
  color: #ff6b35;
  font-size: 1.3rem;
  font-family: "Poppins-Bold";
`;

export const OriginalPrice = styled.span`
  color: #999;
  text-decoration: line-through;
  font-size: 1rem;
`;

export const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const AddToCart = styled.button`
  flex: 1;
  background: #ff6b35;
  color: #ffffff;
  border: none;
  padding: 0.8rem;
  border-radius: 8px;
  font-family: "Poppins-Bold";
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: #e55a2b;
  }
`;

export const WishlistBtn = styled.button`
  background: transparent;
  color: #ff6b35;
  border: 2px solid #ff6b35;
  padding: 0.8rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #ff6b35;
    color: #ffffff;
  }
`;

// --- Loading & Empty states ---
export const LoadingWrap = styled.div`
  text-align: center;
  padding: 3rem;
  width: 100%;
  max-width: 1400px;
  justify-content: center;
  margin: 0 auto;
  align-items: center;
  gap: 10px;
  flex-direction: column;
  display: flex;
  color: #fff;
  font-size: 1.2rem;
`;

export const Spinner = styled.div`
  border: 3px solid #333;
  border-top: 3px solid #ff6b35;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  min-height: 429px;
  color: #cccccc;
  margin-bottom: 2rem;
  > h3 {
    color: #ff6b35;
    margin-bottom: 1rem;
  }
`;

// --- Pagination ---
export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

export const PageBtn = styled.button`
  background: ${({ $active }) => ($active ? "#ff6b35" : "#2c2c2c")};
  color: #ffffff;
  border: 1px solid ${({ $active }) => ($active ? "#ff6b35" : "#333")};
  padding: 0.8rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  min-width: 44px;
  text-align: center;
  font-family: ${({ $active }) =>
    $active ? '"Poppins-Bold"' : '"Poppins-Medium"'};
  transition: all 0.3s ease;

  &:hover {
    background: #ff6b35;
    border-color: #ff6b35;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
  }
`;

export const NavBtn = styled.button`
  background: #2c2c2c;
  color: #ffffff;
  border: 1px solid #333;
  padding: 0.8rem 1.2rem;
  border-radius: 8px;
  cursor: pointer;
  font-family: "Poppins-Bold";
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:disabled {
    background: #333;
    color: #666;
    cursor: not-allowed;
    border-color: #333;
    opacity: 0.5;
  }
  &:not(:disabled):hover {
    background: #ff6b35;
    border-color: #ff6b35;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
  }
`;

export const Ellipsis = styled.span`
  color: #999;
  padding: 0.8rem 0.3rem;
  font-size: 1.2rem;
  font-family: "Poppins-Bold";
  user-select: none;
`;

// Additional legacy components for compatibility
export const CardInfo = styled.div`
  padding: 1.5rem;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  justify-content: space-between;
  width: 100%;
`;

export const CardBandTag = styled.span`
  background: #ff6b35;
  color: #ffffff;
  padding: 0.2rem 0.8rem;
  border-radius: 15px;
  max-width: 12.5rem;
  font-size: 0.8rem;
  font-weight: bold;
  display: inline-block;
  margin-bottom: 0.5rem;
`;

export const CardTitle = styled.h3`
  color: #ffffff;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  font-weight: bold;
`;

export const CardPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  .current {
    color: #ff6b35;
    font-size: 1.2rem;
    font-weight: bold;
  }

  .original {
    color: #999;
    text-decoration: line-through;
    font-size: 1rem;
  }
`;

export const OptionsSection = styled.div``;

export const ActionSection = styled.div`
  padding: 1.5rem 0rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const OptionLabel = styled.label`
  color: #ffffff;
  font-weight: bold;
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.8rem;
`;

export const SizeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20px, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  max-width: 400px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const SizeButton = styled.button`
  background: ${(props) => (props.$selected ? "#ff6b35" : "#2c2c2c")};
  color: #ffffff;
  border: 2px solid ${(props) => (props.$selected ? "#ff6b35" : "#444")};
  padding: 1rem;
  width: 2rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: bold;
  max-height: 2rem;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #ff6b35;
    border-color: #ff6b35;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 107, 53, 0.3);
  }

  &:disabled {
    background: #333;
    color: #666;
    cursor: not-allowed;
    border-color: #333;
    transform: none;
    box-shadow: none;
  }
`;

export const QuantitySection = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-direction: column;
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ActionsSection = styled.div`
  display: flex;
  gap: 0.5rem;
  flex: 1;
  align-items: flex-end;
  justify-content: flex-end;
  margin-right: 1.5rem;
  flex-direction: column;
  margin-bottom: 1.5rem;
`;

export const AddToCartBtn = styled.button`
  background: linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-weight: bold;
  font-size: 0.8rem;
  width: 2.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    background: linear-gradient(135deg, #e55a2b 0%, #ff6b35 100%);
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(255, 107, 53, 0.4);
  }

  &:hover::before {
    left: 100%;
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 1rem;
  }
`;

export const ShareBtn = styled.button`
  background: transparent;
  color: #cccccc;
  border: 2px solid #444;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.5rem;
  width: 2.5rem;

  &:hover {
    background: #333;
    border-color: #ff6b35;
    color: #ff6b35;
    transform: translateY(-2px);
  }
`;

// Mobile Filter Styles
export const FilterButton = styled.button`
  display: none;
  background: #ff6b35;
  color: #ffffff;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-family: "Poppins-Medium";
  font-size: 0.9rem;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    background: #e55a2b;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
  }

  @media (max-width: 1330px) {
    display: flex;
  }
  @media (max-width: 768px) {
    align-self: flex-start;
  }
`;

export const MobileFilterModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  opacity: ${(props) => (props.$isOpen ? "1" : "0")};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s ease-in-out;

  @media (min-width: 1331px) {
    display: none;
  }
`;

export const MobileFilterOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
`;

export const MobileFilterContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #1a1a1a;
  border-radius: 15px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 2px solid #ff6b35;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.8);
`;

export const MobileFilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  background: linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%);
  border-bottom: 1px solid #333;
`;

export const MobileFilterTitle = styled.h2`
  color: #ff6b35;
  font-size: 1.3rem;
  font-family: "Poppins-Bold";
  margin: 0;
`;

export const MobileFilterClose = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 107, 53, 0.1);
    color: #ff6b35;
  }
`;

export const MobileFilterBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;

  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #ff6b35 #1a1a1a;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #1a1a1a;
  }
  &::-webkit-scrollbar-thumb {
    background: #ff6b35;
    border-radius: 3px;
  }
`;
